/**
 * Leaf-description serializer/parser for the DataElementVersion JSON Schema.
 *
 * Real production schemas (admin_buildex_dev) store every leaf property's
 * `description` as a small HTML blob with five labeled fields:
 *
 *   <root>
 *     <div><label>description</label>:<b>{description}</b></div>
 *     <div><label>businessTerm</label>:<b>{businessTerm}</b></div>
 *     <div><label>businessDefinition</label>:<b>{businessDefinition}</b></div>
 *     <div><label>applicableStandardName</label>:<b>{applicableStandardName}</b></div>
 *     <div><label>dataFormat</label>:<b>{dataFormat}</b></div>
 *   </root>
 *
 * The Excel template that authors use to seed schemas defaults `dataFormat`
 * to the literal string "NA" when the source spreadsheet leaves it blank,
 * so we mirror that default on serialize.
 *
 * The two functions in this module are inverses:
 *   parseLeafDescription(html)        => { description, businessTerm, ... }
 *   serializeLeafDescription(record)  => "<root>...</root>"
 *
 * Strings that aren't `<root>...</root>` are passed through unchanged so
 * plain-text descriptions (rare but present in some schemas) survive
 * without being mangled.
 */

export const LEAF_DESC_FIELDS = [
	'description',
	'businessTerm',
	'businessDefinition',
	'applicableStandardName',
	'dataFormat',
];

const DATA_FORMAT_BLANK_DEFAULT = 'NA';

// The production format is generated by an Excel CONCAT formula that
// embeds raw cell values without HTML entity escaping (so `RM&D` stays
// `RM&D`, not `RM&amp;D`). Round-tripping byte-identically requires
// emitting raw values too — escaping here would diverge from every
// existing DataElement and corrupt every leaf description on first save.
//
// Trade-off: a value that contains literal `</b>` or `</div>` would
// break the markup. No production description currently does, and the
// downstream Go preprocessor doesn't HTML-parse this field anyway, so
// the risk is acceptable.
function rawText(value) {
	if (value == null) return '';
	return String(value);
}

/**
 * Build the canonical `<root>...</root>` HTML blob from a 5-field record.
 *   - Missing fields render as empty strings.
 *   - `dataFormat` falls back to `"NA"` when blank, matching the Excel
 *     template convention used to seed all production schemas.
 *   - HTML special characters in field values are escaped so a stray `<`
 *     in a description doesn't break the markup.
 *
 * Returns the empty string when every field is empty so callers can
 * `delete prop.description` in that case (avoid emitting an empty wrapper).
 */
export function serializeLeafDescription(record) {
	const safe = record && typeof record === 'object' ? record : {};
	const dataFormat = safe.dataFormat == null || safe.dataFormat === ''
		? DATA_FORMAT_BLANK_DEFAULT
		: safe.dataFormat;

	const otherFieldsBlank = LEAF_DESC_FIELDS
		.filter((k) => k !== 'dataFormat')
		.every((k) => safe[k] == null || safe[k] === '');
	const dataFormatIsDefault = dataFormat === DATA_FORMAT_BLANK_DEFAULT
		&& (safe.dataFormat == null || safe.dataFormat === '');
	if (otherFieldsBlank && dataFormatIsDefault) return '';

	const part = (label, value) =>
		`<div><label>${label}</label>:<b>${rawText(value)}</b></div>`;

	return [
		'<root>',
		part('description', safe.description ?? ''),
		part('businessTerm', safe.businessTerm ?? ''),
		part('businessDefinition', safe.businessDefinition ?? ''),
		part('applicableStandardName', safe.applicableStandardName ?? ''),
		part('dataFormat', dataFormat),
		'</root>',
	].join('');
}

/**
 * Parse a `<root>...</root>` HTML blob back into a 5-field record. Returns
 * `null` when the input doesn't match the wrapper so the caller can fall
 * back to treating the value as a plain-text description.
 *
 * Tolerant: ignores stray whitespace between elements; accepts label/value
 * order in any sequence; returns `''` for any field the blob is missing.
 */
export function parseLeafDescription(html) {
	if (typeof html !== 'string') return null;
	const trimmed = html.trim();
	if (!trimmed.startsWith('<root>') || !trimmed.endsWith('</root>')) return null;

	const body = trimmed.slice('<root>'.length, -'</root>'.length);
	const out = {};
	for (const k of LEAF_DESC_FIELDS) out[k] = '';

	const divPattern = /<div>\s*<label>([^<]+)<\/label>\s*:\s*<b>([\s\S]*?)<\/b>\s*<\/div>/g;
	const matches = Array.from(body.matchAll(divPattern));
	if (matches.length === 0) return null;
	for (const match of matches) {
		const label = match[1].trim();
		// No HTML entity decoding: production data stores raw characters
		// (no `&amp;` / `&lt;` / etc.) so the captured value is already
		// the literal author-entered text.
		if (LEAF_DESC_FIELDS.includes(label)) {
			out[label] = match[2];
		}
	}
	return out;
}

/**
 * Convenience: inspect any value and tell the caller whether it's the
 * canonical `<root>` blob shape (used to drive UI rendering decisions).
 */
export function isLeafDescriptionBlob(value) {
	return typeof value === 'string' && /^\s*<root>[\s\S]*<\/root>\s*$/.test(value);
}
